import { useRef } from "react";

import { UploadArrowIcon } from "../../../../svg/upload-arrow-icon";

interface FaxDocumentUploadProps {
  /** Currently selected file, or null when nothing is attached yet. */
  file: File | null;
  onFileChange: (file: File | null) => void;
  disabled?: boolean;
}

/**
 * Attach Document control on the Fax tab: a full-width bar that opens the file
 * picker, then shows the chosen file with a remove action.
 *
 * @param props.file - Selected file
 * @param props.onFileChange - Called with the new selection, or null on remove
 * @param props.disabled - Disables picking while a save is in flight
 */
const FaxDocumentUpload = (props: FaxDocumentUploadProps) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleRemove = () => {
    props.onFileChange(null);
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  return (
    <div className="flex flex-col gap-1">
      <label
        htmlFor="appeal-summary-fax-document"
        className="text-small text-primaryGray-3"
      >
        Attach Document
        {/* Matches the oasis inputs' asterisk, whose red is scoped to their own
            containers and so does not reach this label. */}
        <span className="text-[1rem] text-[red]"> *</span>
      </label>

      <input
        ref={inputRef}
        id="appeal-summary-fax-document"
        name="appeal-summary-fax-document"
        type="file"
        className="hidden"
        disabled={props.disabled}
        onChange={(event) =>
          props.onFileChange(event.target.files?.[0] ?? null)
        }
      />

      {props.file ? (
        <div className="flex items-center justify-between gap-3 rounded-md bg-primaryGray-16 px-4 py-3">
          <span className="truncate text-small text-primaryGray-1">
            {props.file.name}
          </span>
          <button
            type="button"
            className="shrink-0 cursor-pointer border-none bg-transparent p-0 text-small font-semibold text-primaryGray-6 hover:text-primaryGray-3"
            onClick={handleRemove}
            disabled={props.disabled}
          >
            Remove
          </button>
        </div>
      ) : (
        <button
          type="button"
          className="flex w-full cursor-pointer items-center gap-2 rounded-md border-none bg-primaryGray-16 px-4 py-3 text-small font-semibold text-blue-600 disabled:cursor-not-allowed disabled:opacity-60"
          onClick={() => inputRef.current?.click()}
          disabled={props.disabled}
        >
          <UploadArrowIcon />
          Upload Document
        </button>
      )}
    </div>
  );
};

export default FaxDocumentUpload;
